import { useI18n } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import { Forms } from '@/enums/Forms';
import { useRoute, useRouter } from 'vue-router';
import { defineComponent, onBeforeMount, onMounted, watch } from 'vue';
import { usePortalStateStore, useUserStore } from '@/plugins/stores';
import NavigationHelper from '@/util/NavigationHelper';
import AuthenticationHelper from '@/util/AuthenticationHelper';

import ActivateForm from '@/components/ActivateForm/ActivateForm.vue';
import ChangePasswordForm from '@/components/ChangePasswordForm/ChangePasswordForm.vue';
import ForgotPasswordForm from '@/components/ForgotPasswordForm/ForgotPasswordForm.vue';
import IDOverview from '@/components/IDOverview/IDOverview.vue';
import PersonalProfile from '@/components/PersonalProfile/PersonalProfile.vue';
import PolteqButton from '@/components/PolteqButton/PolteqButton.vue';
import ResetPasswordForm from '@/components/ResetPasswordForm/ResetPasswordForm.vue';
import SelectServiceForm from '@/components/SelectServiceForm/SelectServiceForm.vue';
import SignInForm from '@/components/SignInForm/SignInForm.vue';
import SignUpForm from '@/components/SignUpForm/SignUpForm.vue';
import TimeOutAlert from '@/components/TimeOutAlert/TimeOutAlert.vue';

export default defineComponent({
  name: 'UserPortal',
  components: {
    ActivateForm,
    ChangePasswordForm,
    ForgotPasswordForm,
    IDOverview,
    PersonalProfile,
    PolteqButton,
    ResetPasswordForm,
    SelectServiceForm,
    SignInForm,
    SignUpForm,
    TimeOutAlert
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const i18n = useI18n();
    let cookieSignInInProgress = false;
    const portalStateStore = usePortalStateStore();
    if (localStorage.getItem('polteq-language') === null) {
      localStorage.setItem('polteq-language', i18n.locale.value);
    } else {
      i18n.locale.value = localStorage.getItem('polteq-language') as string;
    }
    const setLocale = (language: string) => {
      localStorage.setItem('polteq-language', language);
      i18n.locale.value = localStorage.getItem('polteq-language') as string;
    };
    const navigationStore = usePortalStateStore();
    const userStore = useUserStore();
    onBeforeMount(() => {
      watch(
        () => route.query.code,
        async (code) => {
          if (code && code !== '') {
            await AuthenticationHelper.useAuthenticationCode(router, code.toString());
          }
        }
      );
      watch(
        () => route.query.activationCode,
        async (code) => {
          if (code && code !== '') {
            await NavigationHelper.openForm(router, Forms.ACTIVATE, { activationCode: route.query.activationCode });
          }
        }
      );
      watch(
        () => route.query.resetCode,
        async (code) => {
          if (code && code !== '') {
            await NavigationHelper.openForm(router, Forms.RESET_PASSWORD, { resetCode: route.query.resetCode });
          }
        }
      );
      watch(
        () => route.query.client_id,
        async (clientId) => {
          if (clientId && clientId !== '' && !cookieSignInInProgress) {
            cookieSignInInProgress = true;
            const queryParams = new URLSearchParams(window.location.search);
            const redirectUri = queryParams.get('redirect_uri');
            await AuthenticationHelper.cookieSignIn(
              router,
              `?client_id=${clientId.toString()}&redirect_uri=${redirectUri?.toString() ?? ''}`
            );
          }
        }
      );
    });
    onMounted(async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const clientId = queryParams.get('client_id');
      const redirectUri = queryParams.get('redirect_uri');
      if (clientId && redirectUri && !cookieSignInInProgress) {
        cookieSignInInProgress = true;
        await AuthenticationHelper.cookieSignIn(router, window.location.search);
      }

      const route = router.resolve(window.location.pathname);
      portalStateStore.form = route.name ? route.name.toString() : '';
      setInterval(() => {
        portalStateStore.cleanMessages();
      }, 1000);
      if (
        route.name !== Forms.SIGN_UP &&
        route.name !== Forms.FORGOT_PASSWORD &&
        route.name !== Forms.ACTIVATE &&
        route.query.code === undefined &&
        route.query.resetCode === undefined &&
        route.query.activationCode === undefined
      ) {
        const response = await AuthenticationHelper.cookieSignIn(router);
        if (response) {
          await AuthenticationHelper.getUserInfo();
        } else {
          portalStateStore.form = '';
        }
      }
    });
    return {
      i18n,
      router,
      setLocale,
      navigationStore,
      userStore,
      portalStateStore
    };
  },
  computed: {
    AuthenticationHelper() {
      return AuthenticationHelper;
    },
    NavigationHelper() {
      return NavigationHelper;
    },
    Forms() {
      return Forms;
    },
    background() {
      return require(`@/assets/Polteq_BG_${i18n.global.locale.value}.png`) as File;
    }
  }
});
